import React, { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { AlertTriangle, CheckCircle2, X } from 'lucide-react';

/**
 * Reemplazo con diseño Impulza de window.confirm / window.prompt.
 * Uso imperativo, igual que los nativos pero con await:
 *
 *   const ok = await confirmDialog({ title, message, details, list, warning });
 *   const motivo = await promptDialog({ title, label, defaultValue }); // null = cancelado
 *
 * Esc cancela; Enter confirma. Un solo diálogo a la vez por llamada.
 */

const TONES = {
  primary: {
    icon: CheckCircle2,
    iconWrap: 'bg-[#3FF2AA]/15 text-[#0B2A36]',
    button: 'bg-[#0B2A36] text-white hover:bg-[#123d4e] shadow-lg shadow-[#0B2A36]/20',
  },
  danger: {
    icon: AlertTriangle,
    iconWrap: 'bg-rose-100 text-rose-600',
    button: 'bg-rose-500 text-white hover:bg-rose-600 shadow-lg shadow-rose-200',
  },
};

function DialogShell({ mode, opts, onClose }) {
  const {
    title = mode === 'prompt' ? 'Ingresá un dato' : '¿Confirmás?',
    message,
    details = [],
    list,
    warning,
    label,
    defaultValue = '',
    placeholder = '',
    required = false,
    confirmLabel = 'Confirmar',
    cancelLabel = 'Cancelar',
    tone = 'primary',
  } = opts || {};
  const t = TONES[tone] || TONES.primary;
  const Icon = t.icon;
  const [value, setValue] = useState(defaultValue);
  const inputRef = useRef(null);
  const confirmRef = useRef(null);

  const canConfirm = mode !== 'prompt' || !required || value.trim().length > 0;
  const confirm = () => {
    if (!canConfirm) return;
    onClose(mode === 'prompt' ? value.trim() : true);
  };
  const cancel = () => onClose(mode === 'prompt' ? null : false);

  useEffect(() => {
    (mode === 'prompt' ? inputRef.current : confirmRef.current)?.focus();
    if (mode === 'prompt') inputRef.current?.select();
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); cancel(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="fixed inset-0 z-[2000] bg-[#0B2A36]/60 backdrop-blur-sm flex items-center justify-center p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) cancel(); }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="impulza-dialog-title"
    >
      <div className="w-full max-w-md max-h-[90vh] flex flex-col rounded-[2rem] bg-white shadow-2xl overflow-hidden animate-in zoom-in-95">
        <div className="relative px-6 pt-6 pb-4 flex items-start gap-4">
          <div className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 ${t.iconWrap}`}>
            <Icon size={22} />
          </div>
          <div className="min-w-0 pr-6">
            <h3 id="impulza-dialog-title" className="text-sm font-black text-slate-800 uppercase tracking-wide leading-tight">
              {title}
            </h3>
            {message && (
              <p className="mt-1.5 text-xs text-slate-500 font-medium leading-relaxed whitespace-pre-line">{message}</p>
            )}
          </div>
          <button
            type="button"
            onClick={cancel}
            className="absolute right-4 top-4 p-1.5 rounded-xl text-slate-300 hover:text-slate-600 hover:bg-slate-50 transition-colors"
            aria-label="Cerrar"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-6 pb-2 overflow-y-auto space-y-3">
          {details.length > 0 && (
            <div className="rounded-2xl bg-slate-50 border border-slate-100 divide-y divide-slate-100">
              {details.map((d, i) => (
                <div key={i} className="flex items-center justify-between gap-3 px-4 py-2.5">
                  <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">{d.label}</span>
                  <span className="text-[11px] font-bold text-slate-700 text-right">{d.value}</span>
                </div>
              ))}
            </div>
          )}

          {list?.items?.length > 0 && (
            <div>
              {list.title && (
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1.5 ml-1">{list.title}</p>
              )}
              <ul className="rounded-2xl border border-slate-100 px-4 py-2 space-y-1 max-h-48 overflow-y-auto">
                {list.items.map((it, i) => (
                  <li key={i} className="text-[11px] font-semibold text-slate-600 flex gap-2">
                    <span className="text-[#51A7FD]">•</span><span>{it}</span>
                  </li>
                ))}
                {list.more > 0 && (
                  <li className="text-[10px] font-bold text-slate-400 italic">… y {list.more} más</li>
                )}
              </ul>
            </div>
          )}

          {warning?.items?.length > 0 && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
              <p className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-amber-700 mb-1.5">
                <AlertTriangle size={12} /> {warning.title || 'Atención'}
              </p>
              <ul className="space-y-1">
                {warning.items.map((it, i) => (
                  <li key={i} className="text-[11px] font-semibold text-amber-800">{it}</li>
                ))}
                {warning.more > 0 && (
                  <li className="text-[10px] font-bold text-amber-600 italic">… y {warning.more} más</li>
                )}
              </ul>
            </div>
          )}

          {mode === 'prompt' && (
            <div>
              {label && (
                <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1 block mb-1">{label}</label>
              )}
              <input
                ref={inputRef}
                type="text"
                value={value}
                placeholder={placeholder}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); confirm(); } }}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold text-slate-700 outline-none focus:ring-2 focus:ring-[#51A7FD]/10 focus:border-[#51A7FD] shadow-inner"
              />
            </div>
          )}
        </div>

        <div className="px-6 pt-3 pb-6 flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
          <button
            type="button"
            onClick={cancel}
            className="flex-1 rounded-xl py-3 text-slate-500 font-black uppercase text-[10px] tracking-widest border border-slate-200 hover:bg-slate-50 transition-colors"
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={confirm}
            disabled={!canConfirm}
            className={`flex-1 rounded-xl py-3 font-black uppercase text-[10px] tracking-widest transition-all disabled:opacity-40 disabled:cursor-not-allowed ${t.button}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function openDialog(mode, opts) {
  return new Promise((resolve) => {
    const host = document.createElement('div');
    document.body.appendChild(host);
    const root = createRoot(host);
    const prevFocus = document.activeElement;
    const close = (result) => {
      root.unmount();
      host.remove();
      if (prevFocus && typeof prevFocus.focus === 'function') prevFocus.focus();
      resolve(result);
    };
    root.render(<DialogShell mode={mode} opts={opts} onClose={close} />);
  });
}

/** @returns {Promise<boolean>} */
export const confirmDialog = (opts) => openDialog('confirm', opts);

/** @returns {Promise<string|null>} null si se canceló */
export const promptDialog = (opts) => openDialog('prompt', opts);
